<template>
  <div :data-test="`member-${slug}`" class="rounded-t overflow-hidden bg-white">
    <!-- eslint-disable-next-line max-len -->
    <header class="flex items-center px-8 py-2 border-b">
      <div class="flex items-center align-middle" @click="handleShowClick">
        <svg
          class="w-6 h-6 fill-current cursor-pointer"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          ref="toggle"
        >
          <!-- eslint-disable max-len -->
          <path
            v-if="show"
            d="M15.3001 9.30001C15.4927 9.15558 15.7309 9.08546 15.971 9.10252C16.2111 9.11959 16.437 9.22269 16.6072 9.3929C16.7774 9.56311 16.8805 9.78901 16.8976 10.0291C16.9146 10.2692 16.8445 10.5074 16.7001 10.7L12.7001 14.7C12.5132 14.8832 12.2619 14.9859 12.0001 14.9859C11.7383 14.9859 11.487 14.8832 11.3001 14.7L7.3001 10.7C7.15567 10.5074 7.08555 10.2692 7.10261 10.0291C7.11968 9.78901 7.22278 9.56311 7.39299 9.3929C7.5632 9.22269 7.7891 9.11959 8.02921 9.10252C8.26932 9.08546 8.50753 9.15558 8.7001 9.30001L12.0001 12.59L15.3001 9.29001V9.30001Z"
          />
          <path
            v-else
            d="M8.7001 14.7C8.50753 14.8444 8.26932 14.9145 8.02921 14.8975C7.7891 14.8804 7.5632 14.7773 7.39299 14.6071C7.22278 14.4369 7.11968 14.211 7.10261 13.9709C7.08555 13.7308 7.15567 13.4926 7.3001 13.3L11.3001 9.3C11.487 9.11678 11.7383 9.01414 12.0001 9.01414C12.2619 9.01414 12.5132 9.11678 12.7001 9.3L16.7001 13.3C16.8445 13.4926 16.9146 13.7308 16.8976 13.9709C16.8805 14.211 16.7774 14.4369 16.6072 14.6071C16.437 14.7773 16.2111 14.8804 15.971 14.8975C15.7309 14.9145 15.4927 14.8444 15.3001 14.7L12.0001 11.42L8.7001 14.72V14.7Z"
          />
          <!-- eslint-enable max-len -->
        </svg>
      </div>
      <h3 class="ml-8 font-bold text-gray-800">
        {{ name }}
      </h3>
      <m-badge type="BLUE" class="ml-auto">
        {{ formattedAccounts.length }}
      </m-badge>
    </header>
    <transition name="slide">
      <div v-if="!show">
        <div v-if="formattedAccounts.length > 0" class="mt-2 ml-16 px-4 py-6">
          <ul class="space-y-2">
            <template v-for="account in formattedAccounts">
              <member-account-add
                v-if="editAccount && editAccount.identifier === account.identifier"
                :key="`edit-${account.identifier}`"
                :edit-account="editAccount"
                :identifiers="identifiers.filter(i => i !== account.identifier)"
                @cancel="editAccount = {}"
                @update-account="handleUpdateAccount($event, account.identifier)"
              ></member-account-add>
              <member-account-display
                v-else
                :key="`display-${account.identifier}`"
                :account="account"
                @edit="handleEditAccount(account.identifier)"
                @delete="handleRemoveAccount(account.identifier)"
              />
            </template>
          </ul>
        </div>
        <div class="ml-16 pl-3 mt-2 px-4 pt-2 pb-8 bg-blue-100 border border-blue-200 rounded-lg">
          <span class=" font-bold text-sm text-center w-full text-blue-800">Add Account</span>
          <member-account-add
            class="mt-1"
            :identifiers="identifiers"
            @add-account="handleAddAccount"
          ></member-account-add>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import _ from 'lodash';
import { toolkitMixin } from '@/app/mixins/toolkit/item';
import MemberAccountDisplay from './member-account-display.vue';
import MemberAccountAdd, { accountTypeMap } from './member-account-add.vue';

export default {
  name: 'member-account',
  components: {
    MemberAccountDisplay,
    MemberAccountAdd,
  },
  mixins: [toolkitMixin],
  props: {
    uuid: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    accounts: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    show: false,
    editAccount: null,
  }),
  computed: {
    slug() {
      return _.kebabCase(this.name);
    },
    formattedAccounts() {
      const sortedAccounts = _.sortBy(this.accounts, ['label', 'identifier']);
      return sortedAccounts.sort((a, b) => {
        const sortedIndexA = Object.keys(accountTypeMap).findIndex(ele => a.label === ele);
        const sortedIndexB = Object.keys(accountTypeMap).findIndex(ele => b.label === ele);
        if (sortedIndexA > sortedIndexB) return 1;
        if (sortedIndexA < sortedIndexB) return -1;
        return 0;
      });
    },
    identifiers() {
      return this.formattedAccounts.map(account => account.identifier);
    },
  },
  methods: {
    handleShowClick() {
      this.show = !this.show;
    },
    handleAddAccount(account) {
      const { members } = this.getItem;
      const { identifier } = account;
      members[this.uuid].accounts[identifier] = account;
      this.setItem({ members, replace: true });
    },
    handleEditAccount(identifier) {
      this.editAccount = this.accounts[identifier];
    },
    handleUpdateAccount(account, identifier) {
      const { members } = this.getItem;
      if (identifier === account.identifier) {
        members[this.uuid].accounts[identifier] = account;
      } else {
        delete members[this.uuid].accounts[identifier];
        members[this.uuid].accounts[account.identifier] = account;
      }
      this.setItem({ members, rules: [], replace: true });
      this.editAccount = {};
    },
    handleRemoveAccount(identifier) {
      const { members } = this.getItem;
      delete members[this.uuid].accounts[identifier];

      this.setItem({ members, replace: true });
    },
  },
};
</script>

<style></style>
